import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AvisarMedicos } from '../componentes/AvisarMedicos.tsx';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { PainelAgente } from '../componentes/PainelAgente.tsx';
import { Botao, Chapa, type Estagio } from '../componentes/forma.tsx';
import { Lista } from '../componentes/painel.tsx';
import { api, ErroApi, query } from '../lib/api.ts';
import { useOpcoes } from '../lib/cadastro.ts';
import { dataCurta, primeiroDiaDoMes, TIPOS_EXAME, ultimoDiaDoMes } from '../lib/formato.ts';
import { usePode } from '../lib/sessao.tsx';
import './fila.css';

export interface ExameLinha {
  id: string;
  codigo: number;
  dataExame: string;
  tipo: string;
  situacao: Estagio;
  pacienteId: string;
  pacienteNome: string;
  pacienteMatricula: string | null;
  pacienteNascimento: string;
  procedimentoNome: string;
  empresaNome: string | null;
  convenioNome: string | null;
  medicoNome: string | null;
  imagens: number;
  laudoAssinadoEm: string | null;
  modoEnvio: string;
  recusaMotivo: string | null;
}

interface Resposta {
  itens: ExameLinha[];
  total: number;
  pagina: number;
  porPagina: number;
  resumo: Partial<Record<Estagio, number>>;
}

/* Todos vem primeiro porque é onde a recepção procura um exame pelo nome sem
   saber em que etapa ele está. Depois vem a ordem do fluxo. */
const ABAS: { chave: string; rotulo: string; estagio?: Estagio }[] = [
  { chave: 'TODAS', rotulo: 'Todos' },
  { chave: 'AGUARDANDO_EXAME', rotulo: 'Aguardando exame', estagio: 'AGUARDANDO_EXAME' },
  { chave: 'AGUARDANDO_LAUDO', rotulo: 'Aguardando laudo', estagio: 'AGUARDANDO_LAUDO' },
  { chave: 'RECUSADO', rotulo: 'Recusados', estagio: 'RECUSADO' },
  { chave: 'FINALIZADO', rotulo: 'Finalizados', estagio: 'FINALIZADO' },
];

export function Fila() {
  const pode = usePode();
  const podeCadastrar = pode('pode_exame_cadastrar');
  const podeAvisarMedicos = pode('pode_avisar_medicos');
  const navegar = useNavigate();
  const opcoes = useOpcoes();

  /* Os filtros podem vir prontos no endereço. É o que faz o número do painel
     abrir a lista que ele conta -- um contador que leva a uma lista diferente
     do que ele mostrou seria pior do que não levar a lugar nenhum. Serve
     também para guardar nos favoritos e mandar por mensagem. */
  const [params] = useSearchParams();
  const [situacao, setSituacao] = useState(() => params.get('situacao') ?? 'TODAS');
  const [de, setDe] = useState(() => params.get('de') ?? primeiroDiaDoMes());
  const [ate, setAte] = useState(() => params.get('ate') ?? ultimoDiaDoMes());
  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [maisFiltros, setMaisFiltros] = useState(false);
  const [avisarMedicos, setAvisarMedicos] = useState(false);
  const [empresaId, setEmpresaId] = useState('');
  const [procedimentoId, setProcedimentoId] = useState('');
  const [convenioId, setConvenioId] = useState('');
  const [tipo, setTipo] = useState('');
  const [ordem, setOrdem] = useState('DATA');
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAtiva(busca);
      setPagina(1);
    }, 260);
    return () => clearTimeout(t);
  }, [busca]);

  const consulta = query({
    situacao,
    de,
    ate,
    busca: buscaAtiva,
    empresaId,
    procedimentoId,
    convenioId,
    tipo,
    ordem,
    pagina,
  });

  const fila = useQuery({
    queryKey: ['exames', consulta],
    queryFn: ({ signal }) => api.get<Resposta>(`/exames${consulta}`, signal),
    placeholderData: (anterior) => anterior,
    refetchInterval: 60_000, // a fila anda sozinha quando a imagem chega do eFilm
  });

  const resumo = fila.data?.resumo ?? {};
  const contar = (chave: string) => {
    if (chave === 'TODAS') return Object.values(resumo).reduce((s, n) => s + (n ?? 0), 0);
    return resumo[chave as Estagio] ?? 0;
  };

  const total = fila.data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / (fila.data?.porPagina ?? 50)));
  const filtrosExtras = [empresaId, procedimentoId, convenioId, tipo].filter(Boolean).length;

  function limparFiltros() {
    setEmpresaId('');
    setProcedimentoId('');
    setConvenioId('');
    setTipo('');
    setPagina(1);
  }

  return (
    <Layout>
      <Cabecalho titulo="Fila de exames" contagem={fila.isPending ? '' : `${total}`}>
        {podeAvisarMedicos && (
          <Botao
            variante={avisarMedicos ? 'primario' : 'neutro'}
            onClick={() => setAvisarMedicos((v) => !v)}
            aria-expanded={avisarMedicos}
          >
            Avisar médicos
          </Botao>
        )}
        {podeCadastrar && (
          <Botao variante="primario" onClick={() => navegar('/exames/novo')}>
            Novo exame
          </Botao>
        )}
      </Cabecalho>

      {avisarMedicos && podeAvisarMedicos && (
        <div className="fila__avisos">
          <AvisarMedicos />
        </div>
      )}

      {/* O estado do computador da sala de RX fica junto da fila porque é aqui
          que a falta dele aparece: exame cadastrado que não anda é quase sempre
          agente parado. */}
      <PainelAgente />

      <div className="fila__filtros cartao">
        <nav className="abas" aria-label="Estágio do exame">
          {ABAS.map((a) => (
            <button
              key={a.chave}
              className="aba"
              data-ativa={situacao === a.chave || undefined}
              data-estagio={a.estagio}
              onClick={() => {
                setSituacao(a.chave);
                setPagina(1);
              }}
            >
              {a.rotulo}
              <span className="aba__contagem num">{contar(a.chave)}</span>
            </button>
          ))}
        </nav>

        <div className="fila__linha">
          <input
            className="entrada fila__busca"
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Código, paciente, matrícula ou empresa"
            aria-label="Buscar exame"
          />

          <label className="fila__periodo">
            <span className="rotulo">Período</span>
            <span className="fila__periodo-campos">
              <input
                className="entrada"
                type="date"
                value={de}
                onChange={(e) => {
                  setDe(e.target.value);
                  setPagina(1);
                }}
                aria-label="Data inicial"
              />
              <span aria-hidden="true">a</span>
              <input
                className="entrada"
                type="date"
                value={ate}
                onChange={(e) => {
                  setAte(e.target.value);
                  setPagina(1);
                }}
                aria-label="Data final"
              />
            </span>
          </label>

          <Botao
            variante={maisFiltros || filtrosExtras ? 'primario' : 'neutro'}
            onClick={() => setMaisFiltros((v) => !v)}
            aria-expanded={maisFiltros}
          >
            Mais filtros{filtrosExtras ? ` (${filtrosExtras})` : ''}
          </Botao>
        </div>

        {maisFiltros && (
          <div className="fila__avancado">
            <label className="campo campo--terco">
              <span className="campo__rotulo rotulo">Empresa</span>
              <select
                className="entrada entrada--selecao"
                value={empresaId}
                onChange={(e) => {
                  setEmpresaId(e.target.value);
                  setPagina(1);
                }}
              >
                <option value="">Todas</option>
                {opcoes.data?.empresas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="campo campo--terco">
              <span className="campo__rotulo rotulo">Exame</span>
              <select
                className="entrada entrada--selecao"
                value={procedimentoId}
                onChange={(e) => {
                  setProcedimentoId(e.target.value);
                  setPagina(1);
                }}
              >
                <option value="">Todos</option>
                {opcoes.data?.procedimentos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="campo campo--terco">
              <span className="campo__rotulo rotulo">Convênio</span>
              <select
                className="entrada entrada--selecao"
                value={convenioId}
                onChange={(e) => {
                  setConvenioId(e.target.value);
                  setPagina(1);
                }}
              >
                <option value="">Todos</option>
                {opcoes.data?.convenios.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="campo campo--terco">
              <span className="campo__rotulo rotulo">Tipo</span>
              <select
                className="entrada entrada--selecao"
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setPagina(1);
                }}
              >
                <option value="">Todos</option>
                {Object.entries(TIPOS_EXAME).map(([v, r]) => (
                  <option key={v} value={v}>
                    {r}
                  </option>
                ))}
              </select>
            </label>

            <label className="campo campo--terco">
              <span className="campo__rotulo rotulo">Ordenar por</span>
              <select
                className="entrada entrada--selecao"
                value={ordem}
                onChange={(e) => setOrdem(e.target.value)}
              >
                <option value="DATA">Data do exame</option>
                <option value="PACIENTE">Paciente A–Z</option>
                <option value="CODIGO">Código</option>
              </select>
            </label>

            {filtrosExtras > 0 && (
              <div className="fila__limpar">
                <Botao variante="silencioso" tamanho="sm" onClick={limparFiltros}>
                  Limpar filtros
                </Botao>
              </div>
            )}
          </div>
        )}
      </div>

      <Lista
        carregando={fila.isPending}
        recarregando={fila.isFetching}
        erro={fila.error instanceof ErroApi ? fila.error.message : undefined}
        vazio={
          total === 0
            ? {
                titulo: buscaAtiva ? 'Nenhum exame encontrado' : 'Nenhum exame neste estágio',
                apoio: buscaAtiva
                  ? 'Ajuste a busca ou amplie o período.'
                  : 'Mude a aba, amplie o período ou cadastre um exame novo.',
              }
            : undefined
        }
      >
        <table className="tabela tabela--fila">
          <thead>
            <tr>
              <th>Código</th>
              <th className="tabela--num">Data</th>
              <th>Paciente</th>
              <th>Tipo</th>
              <th>Exame</th>
              <th>Empresa / Convênio</th>
              <th>Médico</th>
            </tr>
          </thead>
          <tbody>
            {fila.data?.itens.map((e) => (
              <tr
                key={e.id}
                tabIndex={0}
                role="button"
                onClick={() => navegar(`/exames/${e.id}`)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    navegar(`/exames/${e.id}`);
                  }
                }}
              >
                <td>
                  <Chapa codigo={e.codigo} estagio={e.situacao} />
                </td>
                <td className="tabela--num mono">{dataCurta(e.dataExame)}</td>
                <td className="tabela__nome fila__paciente">
                  {e.pacienteNome}
                  {e.pacienteMatricula && (
                    <span className="tabela__apoio"> · mat. {e.pacienteMatricula}</span>
                  )}
                </td>
                <td>{TIPOS_EXAME[e.tipo] ?? e.tipo}</td>
                <td className="fila__exame" title={e.procedimentoNome}>
                  {e.situacao === 'RECUSADO' && e.recusaMotivo && (
                    <span className="fila__recusa" title={e.recusaMotivo}>
                      Recusado: {e.recusaMotivo}
                    </span>
                  )}
                  {e.procedimentoNome}
                  {e.imagens > 0 && (
                    <span className="fila__imagens" title={`${e.imagens} imagem(ns) no sistema`}>
                      {e.imagens}
                    </span>
                  )}
                </td>
                <td className="fila__empresa" title={e.empresaNome ?? e.convenioNome ?? ''}>
                  {e.empresaNome ?? e.convenioNome ?? '—'}
                </td>
                <td className="fila__medico" title={e.medicoNome ?? ''}>{e.medicoNome ?? '—'}</td>

              </tr>
            ))}
          </tbody>
        </table>
      </Lista>

      {paginas > 1 && (
        <nav className="paginacao" aria-label="Paginação">
          <Botao tamanho="sm" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}>
            Anterior
          </Botao>
          <span className="paginacao__info num">
            Página {pagina} de {paginas}
          </span>
          <Botao tamanho="sm" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
            Próxima
          </Botao>
        </nav>
      )}
    </Layout>
  );
}
